import { useTranslation } from '@jobcost-monorepo/i18n';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import {
  ITimesheetsReportGlobalPrint,
  KeyValue,
  floatToHours,
} from '../helpers';
import type { Dayjs } from 'dayjs';
import dayjs from '@jobcost-monorepo/dayjs-config';
import { MarkType } from '@jobcost-monorepo/database/prisma/enums';

interface Props {
  dataList: ITimesheetsReportGlobalPrint[];
  startDate?: string;
  endDate?: string;
}

const TimesheetGlobalPdf = ({ dataList, startDate, endDate }: Props) => {
  if (dataList.length === 0) {
    return <Document></Document>;
  }
  const { t } = useTranslation('timesheets');
  const start = dayjs.utc(startDate);
  const year = dayjs.utc(startDate).year();
  const month = dayjs
    .utc(startDate)
    .format('MMMM')
    .toString()
    .toLocaleUpperCase();
  const end = dayjs.utc(endDate);
  const defaultName = `REPORT_${month}_${year}.pdf`;

  const dates: Dayjs[] = [];
  let currentDate = start;

  while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
    dates.push(currentDate);
    currentDate = currentDate.add(1, 'day');
  }

  const getContentForDateAndJobOrMarkType = (
    jobOrMarkType: KeyValue | MarkType,
    date: Dayjs,
    report: ITimesheetsReportGlobalPrint,
  ) => {
    const timesheetDate = report.data.groupedDate.find((el) =>
      dayjs.utc(el.operationDate).isSame(date, 'day'),
    );
    if (!timesheetDate) return <Text></Text>;

    const totals = timesheetDate.totalsByJobOrMarkType.find((el) =>
      typeof el.jobOrMarkType === 'object' && typeof jobOrMarkType === 'object'
        ? el.jobOrMarkType.id === jobOrMarkType.id
        : el.jobOrMarkType === jobOrMarkType,
    );

    return <Text>{floatToHours(totals?.totalHours)}</Text>;
  };

  const styles = StyleSheet.create({
    page: {
      padding: 5,
      fontSize: 10,
    },
    // Only the top border lives on the table: when a table is split across
    // pages the fragment on the first page is stretched to the page bottom,
    // so left/right borders here would run below the last row. The top
    // border also keeps the header off y=0, which minPresenceAhead needs.
    table: {
      display: 'flex',
      flexDirection: 'column',
      borderTopWidth: '1px',
      borderColor: 'black',
      margin: 5,
      // compensates the last row's marginBottom -1
      marginBottom: 6,
    },
    title: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      paddingVertical: 8,
    },
    cellContent: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 8,
      paddingHorizontal: 2,
      flexDirection: 'column',
      fontSize: 7,
    },
    cellContentResource: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      paddingVertical: 8,
      paddingLeft: 10,
      paddingRight: 5,
      flexDirection: 'column',
    },
    // Rows draw left and bottom borders, cells only the right one. Data rows
    // also draw a top border; marginBottom -1 makes it overlap the previous
    // row's bottom border (single line). So a row that ends a page is closed
    // at the bottom and the first row of a table continued on the next page
    // is closed on top.
    row: {
      display: 'flex',
      flexDirection: 'row',
      borderLeftWidth: '1px',
      borderBottomWidth: '1px',
      borderColor: 'black',
      marginBottom: -1,
    },
    rowBody: {
      borderTopWidth: '1px',
    },
    cellResource: {
      width: 120,
      borderRightWidth: '1px',
      borderColor: 'black',
    },
    cell: {
      flex: 1,
      borderRightWidth: '1px',
      borderColor: 'black',
    },
    cellTot: {
      width: 30,
      borderRightWidth: '1px',
      borderColor: 'black',
    },
  });

  return (
    <Document title={defaultName}>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.title}>
          <Text>
            {month} {year}
          </Text>
        </View>
        <View>
          {dataList.map((report) => {
            if (report.print) {
              return (
                <View key={report.data.resource.id} style={styles.table}>
                  {/* header */}
                  <View style={styles.row} wrap={false} minPresenceAhead={120}>
                    <View style={styles.cellResource}>
                      <View style={styles.cellContentResource}>
                        <Text>{report.data.resource.name}</Text>
                      </View>
                    </View>
                    {dates.map((date) => {
                      return (
                        <View
                          key={date.format('YYYY-MM-DD')}
                          style={styles.cell}
                        >
                          <View style={styles.cellContent}>
                            <Text>
                              {date.locale('it').format('ddd').toUpperCase()}
                            </Text>
                            <Text>{date.format('DD')}</Text>
                          </View>
                        </View>
                      );
                    })}
                    <View style={styles.cellTot}>
                      <View style={styles.cellContent}>
                        <Text>{t('timesheetReportGlobal.totalShort')}.</Text>
                      </View>
                    </View>
                  </View>
                  {/* content */}
                  {report.data.totalsByJobOrMarkType.map((el, index) => {
                    return (
                      <View
                        style={[styles.row, styles.rowBody]}
                        key={index}
                        wrap={false}
                      >
                        <View style={styles.cellResource}>
                          <View style={styles.cellContentResource}>
                            <Text>
                              {typeof el.jobOrMarkType === 'object'
                                ? el.jobOrMarkType.name
                                : t(`enum.markType.${el.jobOrMarkType}`)}
                            </Text>
                          </View>
                        </View>

                        {dates.map((date) => (
                          <View
                            key={date.format('YYYY-MM-DD')}
                            style={styles.cell}
                          >
                            <View style={styles.cellContent}>
                              {getContentForDateAndJobOrMarkType(
                                el.jobOrMarkType,
                                date,
                                report,
                              )}
                            </View>
                          </View>
                        ))}
                        <View style={styles.cellTot}>
                          <View style={styles.cellContent}>
                            <Text>
                              {floatToHours(el.totalHours)}{' '}
                              {typeof el.jobOrMarkType !== 'object' &&
                                t(
                                  `enum.markTypeShort.${el.jobOrMarkType}`,
                                ).toUpperCase()}
                            </Text>
                          </View>
                        </View>
                      </View>
                    );
                  })}
                </View>
              );
            }
          })}
        </View>
      </Page>
    </Document>
  );
};

export default TimesheetGlobalPdf;
